import type { MessageDescriptor } from "@lingui/core";
import { generateMessageId } from "@lingui/message-utils/generateMessageId";
import { useLingui } from "@lingui/react";
import { legacyMessageSource } from "./locales/legacyMessageSource";

type TOptions = Record<string, unknown> & { defaultValue?: string };

/**
 * Drop-in replacement for react-i18next's useTranslation (Phase 1).
 * Supports legacy keys, `delivery:KEY`, and i18next-style `{{count}}` via Lingui ICU in catalogs.
 */
export function useTranslation(_ns?: string | string[]) {
  const { i18n } = useLingui();

  function t(key: string, options?: TOptions): string {
    const { defaultValue, ...values } = options ?? {};
    const message = legacyMessageSource[key as keyof typeof legacyMessageSource];
    if (message !== undefined) {
      const id = generateMessageId(message, key);
      const desc = { id, message, context: key } as MessageDescriptor;
      return i18n._(desc, values);
    }
    if (defaultValue != null) {
      return String(defaultValue);
    }
    return key;
  }

  return {
    t,
    i18n: {
      language: i18n.locale,
      changeLanguage: async (lng: string) => {
        if (lng === "en" || lng === "es") {
          i18n.activate(lng);
        }
      },
    },
  };
}
